import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { forgeSentencesApi, languagesApi } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Plus,
  Trash2,
  RefreshCw,
  Loader2,
  Volume2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Languages,
  BarChart3,
  Play,
  Pause,
  Edit,
  Save,
  X,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';

const LEVELS = ['iron', 'bronze', 'silver', 'gold'];
const COURSES = [1, 2, 3, 4];
const LESSONS_PER_COURSE = 100;

// Language names for display
const LANGUAGE_NAMES = {
  en: 'English', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian',
  pt: 'Portuguese', ru: 'Russian', zh: 'Chinese', ja: 'Japanese', ko: 'Korean',
  ar: 'Arabic', hi: 'Hindi', tr: 'Turkish', fa: 'Persian', ps: 'Pashto',
  sw: 'Swahili', id: 'Indonesian', th: 'Thai', vi: 'Vietnamese',
};

export default function AdminSentences() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State
  const [sentences, setSentences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [languages, setLanguages] = useState([]);
  
  // Filters
  const [filterCourse, setFilterCourse] = useState('');
  const [filterLesson, setFilterLesson] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  // Add dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addMode, setAddMode] = useState('single'); // 'single' or 'batch'
  const [newCourse, setNewCourse] = useState('1');
  const [newLesson, setNewLesson] = useState('1');
  const [newLevel, setNewLevel] = useState('iron');
  const [newSourceLang, setNewSourceLang] = useState('en');
  const [newSentence, setNewSentence] = useState('');
  const [newBatchSentences, setNewBatchSentences] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Detail dialog
  const [selectedSentence, setSelectedSentence] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [editText, setEditText] = useState('');
  const [retranslateAll, setRetranslateAll] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Audio playback
  const [playingAudio, setPlayingAudio] = useState(null);
  const audioRef = React.useRef(null);

  // Redirect if not admin
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load languages
  useEffect(() => {
    async function loadLanguages() {
      try {
        const res = await languagesApi.getAll();
        setLanguages(res.data || []);
      } catch (err) {
        console.error('Failed to load languages:', err);
      }
    }
    loadLanguages();
  }, []);

  // Load sentences
  const loadSentences = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (filterCourse) params.course_id = parseInt(filterCourse);
      if (filterLesson) params.lesson_id = parseInt(filterLesson);
      if (filterLevel) params.level = filterLevel;
      if (filterStatus) params.status = filterStatus;
      
      const res = await forgeSentencesApi.list(params);
      setSentences(res.data.sentences || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load sentences');
      }
    } finally {
      setLoading(false);
    }
  }, [filterCourse, filterLesson, filterLevel, filterStatus, offset, navigate]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const res = await forgeSentencesApi.getStats();
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => {
    loadSentences();
    loadStats();
  }, [loadSentences, loadStats]);

  // Create sentence(s)
  const handleCreate = async () => {
    if (addMode === 'single') {
      if (!newSentence.trim()) {
        toast.error('Please enter a sentence');
        return;
      }
    } else {
      if (!newBatchSentences.trim()) {
        toast.error('Please enter sentences');
        return;
      }
    }

    setCreating(true);
    try {
      if (addMode === 'single') {
        await forgeSentencesApi.create({
          course_id: parseInt(newCourse),
          lesson_id: parseInt(newLesson),
          level: newLevel,
          source_language: newSourceLang,
          source_text: newSentence.trim(),
        });
        toast.success('Sentence created! Translation processing started.');
      } else {
        const sentenceLines = newBatchSentences
          .split('\n')
          .map(s => s.trim())
          .filter(s => s.length > 0);
        
        if (sentenceLines.length === 0) {
          toast.error('No valid sentences found');
          setCreating(false);
          return;
        }

        await forgeSentencesApi.createBatch({
          course_id: parseInt(newCourse),
          lesson_id: parseInt(newLesson),
          level: newLevel,
          source_language: newSourceLang,
          sentences: sentenceLines,
        });
        toast.success(`${sentenceLines.length} sentences created! Translation processing started.`);
      }
      
      setShowAddDialog(false);
      setNewSentence('');
      setNewBatchSentences('');
      loadSentences();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create sentence(s)');
    } finally {
      setCreating(false);
    }
  };

  // Delete sentence
  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      await forgeSentencesApi.delete(deleteTarget.sentence_id);
      toast.success('Sentence deleted');
      setDeleteTarget(null);
      loadSentences();
      loadStats();
    } catch (err) {
      toast.error('Failed to delete sentence');
    } finally {
      setDeleting(false);
    }
  };

  // Regenerate translations
  const handleRegenerate = async (sentenceId) => {
    try {
      await forgeSentencesApi.regenerate(sentenceId);
      toast.success('Regeneration started!');
      loadSentences();
    } catch (err) {
      toast.error('Failed to regenerate');
    }
  };

  // View sentence details
  const viewDetails = async (sentence) => {
    try {
      const res = await forgeSentencesApi.get(sentence.sentence_id);
      setSelectedSentence(res.data);
      setShowDetailDialog(true);
      setEditMode(false);
      setEditingTranslation(null);
    } catch (err) {
      toast.error('Failed to load sentence details');
    }
  };

  // Start editing a translation
  const startEditTranslation = (langCode, currentText) => {
    setEditingTranslation(langCode);
    setEditText(currentText || '');
    setRetranslateAll(false);
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingTranslation(null);
    setEditText('');
    setRetranslateAll(false);
  };

  // Save translation edit
  const saveTranslationEdit = async () => {
    if (!selectedSentence || !editingTranslation) return;
    
    setSaving(true);
    try {
      await forgeSentencesApi.updateTranslation(
        selectedSentence.sentence_id,
        editingTranslation,
        {
          language_code: editingTranslation,
          text: editText,
          retranslate_all: retranslateAll
        }
      );
      
      if (retranslateAll) {
        toast.success(`Translation saved. Retranslating all languages from ${LANGUAGE_NAMES[editingTranslation] || editingTranslation}...`);
      } else {
        toast.success('Translation saved');
      }
      
      // Reload sentence details
      const res = await forgeSentencesApi.get(selectedSentence.sentence_id);
      setSelectedSentence(res.data);
      cancelEdit();
      loadSentences();
    } catch (err) {
      toast.error('Failed to save translation');
    } finally {
      setSaving(false);
    }
  };

  // Generate audio for a translation
  const generateAudio = async (langCode) => {
    if (!selectedSentence) return;
    
    try {
      toast.info(`Generating audio for ${LANGUAGE_NAMES[langCode] || langCode}...`);
      await forgeSentencesApi.generateAudio(selectedSentence.sentence_id, langCode);
      toast.success('Audio generated!');
      
      // Reload sentence details
      const res = await forgeSentencesApi.get(selectedSentence.sentence_id);
      setSelectedSentence(res.data);
    } catch (err) {
      toast.error('Failed to generate audio');
    }
  };

  // Play audio
  const playAudio = (sentenceId, languageCode) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    const audioUrl = forgeSentencesApi.getAudio(sentenceId, languageCode);
    audioRef.current = new Audio(audioUrl);
    audioRef.current.play();
    setPlayingAudio(`${sentenceId}-${languageCode}`);
    
    audioRef.current.onended = () => setPlayingAudio(null);
    audioRef.current.onerror = () => {
      toast.error('Failed to play audio');
      setPlayingAudio(null);
    };
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingAudio(null);
  };

  // Render level badge
  const renderLevelBadge = (level) => {
    const colors = {
      iron: 'bg-gray-500',
      bronze: 'bg-amber-700',
      silver: 'bg-gray-300 text-gray-800',
      gold: 'bg-yellow-500 text-yellow-900',
    };
    return (
      <Badge className={`${colors[level]} text-xs`}>
        {level}
      </Badge>
    );
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    if (status === 'ready') {
      return <Badge className="bg-green-600">Ready</Badge>;
    } else if (status === 'processing') {
      return <Badge className="bg-blue-600 animate-pulse">Processing</Badge>;
    } else {
      return <Badge variant="destructive">Error</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Forge Sentences</h1>
            <p className="text-muted-foreground">
              Manage lesson sentences with auto-translation and audio
            </p>
          </div>
          <Button onClick={() => setShowAddDialog(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Sentences
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Sentences
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Ready
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.ready}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Processing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.processing}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  By Level
                </CardTitle>
              </CardHeader>
              <CardContent className="flex gap-2 flex-wrap">
                {stats.by_level && Object.entries(stats.by_level).map(([level, count]) => (
                  <span key={level} className="text-xs">
                    {level}: {count}
                  </span>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Select value={filterCourse || "all"} onValueChange={(v) => { setFilterCourse(v === "all" ? '' : v); setOffset(0); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Course" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Courses</SelectItem>
                  {COURSES.map(c => (
                    <SelectItem key={c} value={String(c)}>Course {c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterLesson || "all"} onValueChange={(v) => { setFilterLesson(v === "all" ? '' : v); setOffset(0); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Lesson" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Lessons</SelectItem>
                  {Array.from({ length: LESSONS_PER_COURSE }, (_, i) => i + 1).map(l => (
                    <SelectItem key={l} value={String(l)}>Lesson {l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterLevel || "all"} onValueChange={(v) => { setFilterLevel(v === "all" ? '' : v); setOffset(0); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  {LEVELS.map(l => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterStatus || "all"} onValueChange={(v) => { setFilterStatus(v === "all" ? '' : v); setOffset(0); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" onClick={() => {
                setFilterCourse('');
                setFilterLesson('');
                setFilterLevel('');
                setFilterStatus('');
                setOffset(0);
              }}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sentences Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : sentences.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No sentences found. Add some to get started!
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Course</TableHead>
                    <TableHead className="w-[100px]">Lesson</TableHead>
                    <TableHead className="w-[100px]">Level</TableHead>
                    <TableHead>Source Text</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead className="w-[100px]">Languages</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sentences.map((sentence) => (
                    <TableRow key={sentence.sentence_id}>
                      <TableCell>{sentence.course_id}</TableCell>
                      <TableCell>{sentence.lesson_id}</TableCell>
                      <TableCell>{renderLevelBadge(sentence.level)}</TableCell>
                      <TableCell 
                        className="max-w-[300px] truncate cursor-pointer hover:text-primary"
                        onClick={() => viewDetails(sentence)}
                      >
                        {sentence.source_text}
                      </TableCell>
                      <TableCell>{renderStatusBadge(sentence.status)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {sentence.languages_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => viewDetails(sentence)}
                            title="View details"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleRegenerate(sentence.sentence_id)}
                            title="Regenerate translations"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setDeleteTarget(sentence)}
                            title="Delete"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
          
          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between p-4 border-t">
              <span className="text-sm text-muted-foreground">
                Showing {offset + 1}-{Math.min(offset + limit, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + limit >= total}
                  onClick={() => setOffset(offset + limit)}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Add Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Sentences</DialogTitle>
            </DialogHeader>
            
            <Tabs value={addMode} onValueChange={setAddMode}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="single">Single</TabsTrigger>
                <TabsTrigger value="batch">Batch</TabsTrigger>
              </TabsList>
              
              <div className="space-y-4 mt-4">
                {/* Common fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Course</label>
                    <Select value={newCourse} onValueChange={setNewCourse}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COURSES.map(c => (
                          <SelectItem key={c} value={String(c)}>Course {c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Lesson</label>
                    <Select value={newLesson} onValueChange={setNewLesson}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: LESSONS_PER_COURSE }, (_, i) => i + 1).map(l => (
                          <SelectItem key={l} value={String(l)}>Lesson {l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Level</label>
                    <Select value={newLevel} onValueChange={setNewLevel}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVELS.map(l => (
                          <SelectItem key={l} value={l}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Source Language</label>
                    <Select value={newSourceLang} onValueChange={setNewSourceLang}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {languages.map(lang => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.name} ({lang.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <TabsContent value="single" className="mt-0">
                  <div>
                    <label className="text-sm font-medium">Sentence</label>
                    <Input
                      value={newSentence}
                      onChange={(e) => setNewSentence(e.target.value)}
                      placeholder="Enter the sentence..."
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="batch" className="mt-0">
                  <div>
                    <label className="text-sm font-medium">Sentences (one per line)</label>
                    <Textarea
                      value={newBatchSentences}
                      onChange={(e) => setNewBatchSentences(e.target.value)}
                      placeholder="Enter sentences, one per line..."
                      rows={8}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Max 50 sentences per batch
                    </p>
                  </div>
                </TabsContent>
              </div>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={creating}>
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog with Edit */}
        <Dialog open={showDetailDialog} onOpenChange={(open) => {
          setShowDetailDialog(open);
          if (!open) {
            cancelEdit();
          }
        }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Sentence Details</span>
                {selectedSentence?.theme && (
                  <Badge variant="outline">{selectedSentence.theme}</Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {selectedSentence && (
              <div className="space-y-4">
                {/* Metadata */}
                <div className="grid grid-cols-5 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Course:</span> {selectedSentence.course_id}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Lesson:</span> {selectedSentence.lesson_id}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Sentence #:</span> {selectedSentence.sentence_num || 'N/A'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Level:</span> {renderLevelBadge(selectedSentence.level)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span> {renderStatusBadge(selectedSentence.status)}
                  </div>
                </div>
                
                {/* Source Sentence */}
                <div className="p-3 bg-muted rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">
                    Source ({LANGUAGE_NAMES[selectedSentence.source_language] || selectedSentence.source_language})
                  </div>
                  <div className="font-medium text-lg">{selectedSentence.source_text}</div>
                </div>
                
                {/* Translations */}
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Languages className="w-4 h-4" />
                    Translations ({Object.keys(selectedSentence.translations || {}).length})
                    <span className="text-xs text-muted-foreground ml-2">Click edit to modify</span>
                  </h4>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                    {Object.entries(selectedSentence.translations || {})
                      .sort((a, b) => {
                        // Sort: source language first, then alphabetically
                        if (a[0] === selectedSentence.source_language) return -1;
                        if (b[0] === selectedSentence.source_language) return 1;
                        return (LANGUAGE_NAMES[a[0]] || a[0]).localeCompare(LANGUAGE_NAMES[b[0]] || b[0]);
                      })
                      .map(([langCode, data]) => (
                      <div 
                        key={langCode} 
                        className={`p-3 rounded-lg border ${
                          langCode === selectedSentence.source_language 
                            ? 'bg-primary/10 border-primary/30' 
                            : 'bg-muted/50'
                        }`}
                      >
                        {editingTranslation === langCode ? (
                          // Edit Mode
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm">
                                {LANGUAGE_NAMES[langCode] || langCode}
                                <span className="font-mono text-xs text-muted-foreground ml-2">({langCode})</span>
                              </span>
                            </div>
                            <Textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              rows={2}
                              className="text-sm"
                              placeholder="Enter translation..."
                            />
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <Switch
                                  id="retranslate-all"
                                  checked={retranslateAll}
                                  onCheckedChange={setRetranslateAll}
                                />
                                <Label htmlFor="retranslate-all" className="text-xs">
                                  Use as source & retranslate all
                                </Label>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={cancelEdit}
                                  disabled={saving}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={saveTranslationEdit}
                                  disabled={saving || !editText.trim()}
                                >
                                  {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                                  <Save className="w-4 h-4 mr-1" />
                                  Save
                                </Button>
                              </div>
                            </div>
                            {retranslateAll && (
                              <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950 p-2 rounded">
                                ⚠️ This will use this translation as the new source and regenerate ALL other translations.
                              </p>
                            )}
                          </div>
                        ) : (
                          // View Mode
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm">
                                  {LANGUAGE_NAMES[langCode] || langCode}
                                </span>
                                <span className="font-mono text-xs text-muted-foreground">({langCode})</span>
                                {langCode === selectedSentence.source_language && (
                                  <Badge variant="secondary" className="text-xs">Source</Badge>
                                )}
                                {data.source === 'manual_edit' && (
                                  <Badge variant="outline" className="text-xs">Edited</Badge>
                                )}
                              </div>
                              <div className="text-sm">
                                {data.text || <span className="text-muted-foreground italic">Translation not available</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {data.has_audio ? (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => {
                                    if (playingAudio === `${selectedSentence.sentence_id}-${langCode}`) {
                                      stopAudio();
                                    } else {
                                      playAudio(selectedSentence.sentence_id, langCode);
                                    }
                                  }}
                                  title="Play audio"
                                >
                                  {playingAudio === `${selectedSentence.sentence_id}-${langCode}` ? (
                                    <Pause className="w-4 h-4" />
                                  ) : (
                                    <Volume2 className="w-4 h-4" />
                                  )}
                                </Button>
                              ) : data.text ? (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => generateAudio(langCode)}
                                  title="Generate audio"
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <Volume2 className="w-4 h-4 opacity-50" />
                                </Button>
                              ) : null}
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => startEditTranslation(langCode, data.text)}
                                title="Edit translation"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Sentence?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this sentence and all its translations.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
